import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon } from 'lucide-react';
type ModalType = 'privacy' | 'terms' | null;
const privacyContent = {
  title: 'Privacy Policy',
  lastUpdated: 'Last updated: March 6, 2026',
  sections: [{
    heading: 'Information We Collect',
    body: 'We collect information you provide directly to us, such as your name, email address, and payment information when you purchase the Back To You program. We also automatically collect certain information about your device and usage of our services.'
  }, {
    heading: 'How We Use Your Information',
    body: 'We use the information we collect to provide, maintain, and improve our services, process transactions, send you program materials, and communicate with you about updates, promotions, and support.'
  }, {
    heading: 'Information Sharing',
    body: 'We do not sell, trade, or otherwise transfer your personal information to third parties. This does not include trusted third parties who assist us in operating our website, conducting our business, or servicing you, so long as those parties agree to keep this information confidential.'
  }, {
    heading: 'Data Security',
    body: 'We implement a variety of security measures to maintain the safety of your personal information. Your personal information is contained behind secured networks and is only accessible by a limited number of persons who have special access rights.'
  }, {
    heading: 'Cookies',
    body: 'We use cookies to understand and save your preferences for future visits, keep track of advertisements, and compile aggregate data about site traffic and site interaction so that we can offer better experiences and tools in the future.'
  }, {
    heading: 'Your Rights',
    body: 'You have the right to access, correct, or delete your personal data at any time. You may also opt out of marketing communications by following the unsubscribe instructions in any email we send.'
  }, {
    heading: 'Changes to This Policy',
    body: 'We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last updated" date.'
  }]
};
const termsContent = {
  title: 'Terms of Service',
  lastUpdated: 'Last updated: March 6, 2026',
  sections: [{
    heading: 'Acceptance of Terms',
    body: 'By accessing and using the Back To You program and website, you accept and agree to be bound by the terms and provisions of this agreement. If you do not agree to these terms, please do not use our services.'
  }, {
    heading: 'Program Description',
    body: 'Back To You is a digital postpartum identity and body restoration program. The program includes educational content, exercise protocols, nutrition guidance, and mindset practices. Results may vary and are not guaranteed.'
  }, {
    heading: 'Medical Disclaimer',
    body: 'The content provided in Back To You is for informational and educational purposes only and is not intended as medical advice. Always consult with a qualified healthcare provider before beginning any exercise or nutrition program, especially postpartum. If you experience any pain or difficulty with the exercises or advice, stop and consult your healthcare provider.'
  }, {
    heading: 'Intellectual Property',
    body: 'All content included in the Back To You program — including text, graphics, logos, images, audio, video, and software — is the property of Back To You and is protected by copyright and intellectual property laws. You may not reproduce, distribute, or create derivative works without express written permission.'
  }, {
    heading: 'User Responsibilities',
    body: 'You are responsible for maintaining the confidentiality of your account information and for all activities that occur under your account. You agree not to share, resell, or redistribute program materials.'
  }, {
    heading: 'Refund Policy',
    body: 'We offer a satisfaction guarantee as described on our sales page. If you are not satisfied with the program after implementing it for 14 days, you may request a refund by contacting our support team with details of your experience.'
  }, {
    heading: 'Limitation of Liability',
    body: 'Back To You shall not be liable for any indirect, incidental, special, consequential, or punitive damages resulting from your use of or inability to use the program. Our total liability shall not exceed the amount you paid for the program.'
  }, {
    heading: 'Governing Law',
    body: 'These terms shall be governed by and construed in accordance with applicable laws, without regard to conflict of law principles.'
  }]
};
export function Footer() {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  const content = activeModal === 'privacy' ? privacyContent : termsContent;
  return <>
      <footer className="bg-[#2D2D2D] border-t border-white/10 px-4 py-8 sm:px-6 lg:px-8" data-id="element-60">
        <div className="mx-auto max-w-3xl flex flex-col items-center gap-4" data-id="element-61">
          <div className="flex items-center gap-6" data-id="element-62">
            <button onClick={() => setActiveModal('privacy')} className="font-sans text-sm text-white/50 underline-offset-4 transition-colors hover:text-white hover:underline" data-id="element-63">
              Privacy Policy
            </button>
            <span className="text-white/20" data-id="element-64">|</span>
            <button onClick={() => setActiveModal('terms')} className="font-sans text-sm text-white/50 underline-offset-4 transition-colors hover:text-white hover:underline" data-id="element-65">
              Terms of Service
            </button>
          </div>
          <p className="font-sans text-xs text-white/30" data-id="element-66">
            © {new Date().getFullYear()} Back To You. All rights reserved.
          </p>
        </div>
      </footer>

      <AnimatePresence data-id="element-67">
        {activeModal && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm" onClick={() => setActiveModal(null)} data-id="element-68">
            <motion.div initial={{
          opacity: 0,
          y: 20,
          scale: 0.97
        }} animate={{
          opacity: 1,
          y: 0,
          scale: 1
        }} exit={{
          opacity: 0,
          y: 20,
          scale: 0.97
        }} transition={{
          duration: 0.3,
          ease: 'easeOut'
        }} onClick={e => e.stopPropagation()} className="relative max-h-[85vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-[#FBF7F0] p-8 sm:p-10 shadow-2xl" data-id="element-69">
              <button onClick={() => setActiveModal(null)} className="absolute right-4 top-4 rounded-full p-2 text-[#2D2D2D]/50 transition-colors hover:bg-[#2D2D2D]/10 hover:text-[#2D2D2D]" aria-label="Close" data-id="element-70">
                <XIcon className="h-5 w-5" data-id="element-71" />
              </button>

              <h2 className="mb-2 font-serif text-3xl font-bold text-[#2D2D2D]" data-id="element-72">
                {content.title}
              </h2>
              <p className="mb-8 font-sans text-sm text-[#2D2D2D]/50" data-id="element-73">
                {content.lastUpdated}
              </p>

              <div className="space-y-6" data-id="element-74">
                {content.sections.map((section, index) => <div key={index} data-id="element-75">
                    <h3 className="mb-2 font-serif text-lg font-bold text-[#2D2D2D]" data-id="element-76">
                      {section.heading}
                    </h3>
                    <p className="font-sans text-base leading-relaxed text-[#2D2D2D]/80" data-id="element-77">
                      {section.body}
                    </p>
                  </div>)}
              </div>

              <div className="mt-8 border-t border-[#2D2D2D]/10 pt-6 text-center" data-id="element-78">
                <button onClick={() => setActiveModal(null)} className="rounded-full bg-[#D4603A] px-6 py-2.5 font-sans text-sm font-semibold text-white transition-colors hover:bg-[#C0522E]" data-id="element-79">
                  Close
                </button>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </>;
}